import { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next'

import { NormalizedCacheObject } from '@apollo/client'
import { CMSBannerData } from 'banner/Banner'
import { Request, Response } from 'express'
import { getCanonicalUrl } from 'head/utils/seoHelpers'
import { serverRequestService } from 'server/services/serverRequestService'
import { logger } from 'server/utils/logger'
import createApolloClient from 'shared/apollo/createApolloClient'
import { sizes } from 'shared/lib/media'
import { parse } from 'shared/lib/safeJSON'
import { Breadcrumb, Currency, FeatureFlags, ShoppingRegionData } from 'shared/types'
import { sortBreadcrumbChildren } from 'shared/utils/breadcrumb-utils'

export type FCGetServerSidePropsContext = GetServerSidePropsContext & {
  req: Request &
    NextApiRequest & {
      /** Banner object set in the server request object by the bannerInjector middleware */
      cmsBanner: string
      /** Active raffles JSON set by the raffleInjector middleware */
      activeRaffles?: string
    }
  res: Response & NextApiResponse
}

export type GetServerPageProps = {
  /** Used by withApolloClient HOC. TODO: Check use and need.  */
  apolloState: NormalizedCacheObject
  /** Array of currencies */
  availableCurrencies: Currency[]
  /** Canonical URL for current page */
  canonicalUrl: string
  /** Banner object set in the server request object by the bannerInjector middleware */
  cmsBanner: CMSBannerData
  /** Active raffles JSON set by the raffleInjector middleware */
  activeRaffles: string
  /** Used by withApolloClient HOC. TODO: Check use and need. */
  csrf: string | null
  /** Used by withApolloClient HOC. TODO: Check use and need. */
  currency: string
  /** Normalized version of the request URL that strips the _next/data prefix
   * for client transitions and includes original query values. */
  currentPath: string
  /** Has auth cookie  */
  isAuthenticated: boolean
  /** Feature Flags object set in req object by the feature flags middleware */
  featureFlags: FeatureFlags
  /** Footer object  */
  footerData: Breadcrumb
  /** Active locale */
  locale: string
  /** Shopping region object for the ShoppingRegionContextProvider */
  shoppingRegionData: ShoppingRegionData
  /** SSR width used to determine the initial breakpoint for the responsive design */
  initWidth: number
}

/**
 * This function is intended to be used in all Pages that fetch data from the server.
 * It returns the props used in the custom App component.
 * @param context  - augemented Next.js GetServerSidePropsContext
 */
export const getServerPageProps = async (
  context: FCGetServerSidePropsContext,
): Promise<GetServerPageProps> => {
  const { req, res, resolvedUrl, locale, defaultLocale, locales } = context
  const activeLocale = locale || defaultLocale || 'en'
  const {
    activeRaffles: reqActiveRaffles,
    cmsBanner: reqCmsBanner,
    cookies,
    featureFlags = {},
    originalUrl,
    availableCurrencies,
    shoppingRegionData,
  } = req
  const token: string = cookies.jwt
  const csrf = req.csrfToken ? req.csrfToken() : null
  const currency = cookies?.currency || null
  const cmsBanner = parse(reqCmsBanner) as CMSBannerData
  const canonicalUrl = getCanonicalUrl(
    req,
    resolvedUrl,
    originalUrl,
    activeLocale,
    defaultLocale || '',
    locales || [],
  )

  const isAuthenticated = !!cookies?.jwt

  let footerData: Breadcrumb = { children: [], name: '', slug: '' }

  // Fetch footer breadcrumb data
  try {
    const { data } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/breadcrumbs/footer?includeChildren=true',
    )
    footerData = sortBreadcrumbChildren(data)
  } catch (error) {
    logger.error(error, 'Error fetching footer data.')
  }

  // Set width when SSR
  // Mobile automation sends a is_mobile cookie so that we can force mobile sizing when SSR
  const isMobileAutomation = cookies.is_mobile ?? false
  const initWidth = !isMobileAutomation ? 0 : sizes.extraSmall
  const apolloState = getApolloClientState(context, token, csrf, currency)

  return {
    activeRaffles: reqActiveRaffles || '[]',
    apolloState,
    availableCurrencies,
    canonicalUrl,
    cmsBanner,
    csrf,
    currency,
    currentPath: resolvedUrl,
    isAuthenticated,
    featureFlags,
    footerData,
    locale: activeLocale,
    shoppingRegionData,
    initWidth,
  }
}

/**
 * Used by withApolloClient HOC. TODO: Check use and need.
 */
const getApolloClientState = (
  context: FCGetServerSidePropsContext,
  token: string,
  csrf: string | null,
  currency: string,
) => {
  const { req, res } = context
  const getCsrf = () => csrf
  const getToken = () => token
  const apollo = createApolloClient(
    {},
    {
      getCsrf,
      getToken,
      req,
      res,
      currency,
    },
  )

  // Extract query data from the Apollo store
  const apolloState: NormalizedCacheObject = apollo.cache.extract()

  return apolloState
}
